import React from 'react';

interface TimelineEvent {
  id: string;
  type: 'initiated' | 'validated' | 'signed' | 'executed' | 'completed' | 'failed';
  title: string;
  description: string;
  timestamp: string;
  txHash?: string;
  blockNumber?: number;
  status: 'completed' | 'current' | 'pending' | 'failed';
}

interface TransactionTimelineProps {
  events: TimelineEvent[];
}

const TransactionTimeline: React.FC<TransactionTimelineProps> = ({ events }) => {
  const getEventIcon = (type: string, status: string) => {
    const iconClass = status === 'completed' 
      ? 'text-white bg-green-500' 
      : status === 'current' 
      ? 'text-white bg-blue-500' 
      : status === 'failed'
      ? 'text-white bg-red-500'
      : 'text-gray-400 bg-gray-200';

    switch (type) {
      case 'initiated':
        return (
          <div className={`flex h-8 w-8 items-center justify-center rounded-full ${iconClass}`}>
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
          </div>
        );
      case 'validated':
        return (
          <div className={`flex h-8 w-8 items-center justify-center rounded-full ${iconClass}`}>
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        );
      case 'signed':
        return (
          <div className={`flex h-8 w-8 items-center justify-center rounded-full ${iconClass}`}>
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
          </div>
        );
      case 'executed':
        return (
          <div className={`flex h-8 w-8 items-center justify-center rounded-full ${iconClass}`}>
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
        );
      case 'completed':
        return (
          <div className={`flex h-8 w-8 items-center justify-center rounded-full ${iconClass}`}>
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        );
      case 'failed':
        return (
          <div className={`flex h-8 w-8 items-center justify-center rounded-full ${iconClass}`}>
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
        );
      default:
        return (
          <div className={`flex h-8 w-8 items-center justify-center rounded-full ${iconClass}`}>
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        );
    }
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">Transaction Timeline</h3>
        <p className="text-sm text-gray-500 mt-1">
          Track the progress of this bridge transaction through each stage
        </p>
      </div>

      <div className="px-6 py-6">
        {events.length === 0 ? (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="mt-4 text-sm font-medium text-gray-900">No timeline events</h3>
            <p className="mt-2 text-sm text-gray-500">
              Timeline events will appear here as the transaction progresses.
            </p>
          </div>
        ) : (
          <div className="flow-root">
            <ul className="-mb-8">
              {events.map((event, eventIdx) => (
                <li key={event.id}>
                  <div className="relative pb-8">
                    {eventIdx !== events.length - 1 ? (
                      <span 
                        className={`absolute top-4 left-4 -ml-px h-full w-0.5 ${
                          event.status === 'completed' ? 'bg-green-500' : 'bg-gray-300'
                        }`} 
                        aria-hidden="true" 
                      />
                    ) : null}
                    
                    <div className="relative flex space-x-3">
                      <div>
                        {getEventIcon(event.type, event.status)}
                      </div>
                      
                      <div className="flex min-w-0 flex-1 justify-between space-x-4 pt-1.5">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <p className="text-sm font-medium text-gray-900">
                              {event.title}
                            </p>
                            {event.status === 'current' && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                In Progress
                              </span>
                            )}
                            {event.status === 'failed' && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                Failed
                              </span>
                            )}
                          </div>
                          
                          <p className="text-sm text-gray-600 mt-1">
                            {event.description}
                          </p>

                          {/* Transaction Details */}
                          {(event.txHash || event.blockNumber) && (
                            <div className="mt-2 space-y-1">
                              {event.txHash && (
                                <div className="flex items-center space-x-2">
                                  <span className="text-xs text-gray-500">Tx:</span>
                                  <code className="text-xs text-gray-700 bg-gray-100 px-1 py-0.5 rounded">
                                    {event.txHash.slice(0, 10)}...{event.txHash.slice(-8)}
                                  </code>
                                  <button
                                    onClick={() => copyToClipboard(event.txHash!)}
                                    className="text-gray-400 hover:text-gray-600"
                                  >
                                    <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                    </svg>
                                  </button>
                                </div>
                              )}
                              
                              {event.blockNumber && (
                                <div className="flex items-center space-x-2">
                                  <span className="text-xs text-gray-500">Block:</span>
                                  <span className="text-xs text-gray-700">
                                    #{event.blockNumber.toLocaleString()}
                                  </span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        
                        <div className="whitespace-nowrap text-right text-sm text-gray-500">
                          <time dateTime={event.timestamp}>
                            {formatTimestamp(event.timestamp)}
                          </time>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Progress Summary */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span className="text-gray-600">
                  {events.filter(e => e.status === 'completed').length} completed
                </span>
              </div>
              
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                <span className="text-gray-600">
                  {events.filter(e => e.status === 'current').length} in progress
                </span>
              </div>
              
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-gray-300 rounded-full"></div>
                <span className="text-gray-600">
                  {events.filter(e => e.status === 'pending').length} pending
                </span>
              </div>

              {events.some(e => e.status === 'failed') && (
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                  <span className="text-gray-600">
                    {events.filter(e => e.status === 'failed').length} failed
                  </span>
                </div>
              )}
            </div>

            <div className="text-gray-500">
              {events.length > 0 && (
                <span>
                  Progress: {Math.round((events.filter(e => e.status === 'completed').length / events.length) * 100)}%
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransactionTimeline; 